import { SentryApiClient } from '_generated/api';
import React, { createContext, useContext, useMemo } from 'react';
import { SiteThemeConfig, getThemeConfig } from 'Sites/themes';

export interface SiteThemeContextValue {
  /** The current site's theme type */
  theme: SentryApiClient.UIThemeType;
  /** The theme configuration (null for Default theme) */
  config: SiteThemeConfig | null;
  /** Whether this is a styled theme (not Default) */
  isStyled: boolean;
  /** CSS data attribute value for scoping styles */
  cssId: string | null;
}

const SiteThemeContext = createContext<SiteThemeContextValue | null>(null);

export interface SiteThemeProviderProps {
  /** The theme type for the current site */
  theme: SentryApiClient.UIThemeType | undefined | null;
  children: React.ReactNode;
}

/**
 * Provides the current site's theme context to child components.
 * Wrap site detail pages with this provider to enable theme-aware styling.
 */
export const SiteThemeProvider: React.FC<SiteThemeProviderProps> = ({
  theme,
  children,
}) => {
  const resolvedTheme = theme ?? SentryApiClient.UIThemeType.Default;
  const config = getThemeConfig(resolvedTheme);

  const value = useMemo<SiteThemeContextValue>(
    () => ({
      theme: resolvedTheme,
      config,
      isStyled: config !== null,
      cssId: config?.cssId ?? null,
    }),
    [resolvedTheme, config]
  );

  return (
    <SiteThemeContext.Provider value={value}>
      {children}
    </SiteThemeContext.Provider>
  );
};

/**
 * Hook to access the current site's theme context.
 * Must be used within a SiteThemeProvider.
 */
export function useSiteTheme(): SiteThemeContextValue {
  const result = useContext(SiteThemeContext);

  if (!result) {
    throw new Error(
      'useSiteTheme() cannot be used without being wrapped by a SiteThemeProvider'
    );
  }

  return result;
}

/**
 * Hook to optionally access the current site's theme context.
 * Returns null if not within a SiteThemeProvider (safe to use in shared components).
 */
export function useSiteThemeOptional(): SiteThemeContextValue | null {
  return useContext(SiteThemeContext);
}
